import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildTranscriptIndex, locateEvidence } from '../../common/evidenceLocator';
import { customerSpeakerOf, sideOf } from '../../common/speakers';
import { Formatter } from '../../format';

const FLASH_MS = 1800;

// Smooth scrolling unless the viewer asked for less motion.
const scrollBehaviour = () =>
  (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'auto' : 'smooth');

// The next frame, once a just-opened Transcript section is visible (a hidden turn cannot be scrolled to).
const nextFrame = (fn) =>
  (typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(fn) : setTimeout(fn, 16));

/**
 * Evidence quotes as links to their transcript turns (ported from the hl line's call page).
 *
 * `evidenceAt(ev)` matches a quote to a turn in the browser — a reviewer's citation by the line it names, an
 * AI quote by its words, in the text or its English — and returns `{ speaker, time, isAgent, onClick }`, or
 * null when it cannot be placed confidently (the quote then stays plain text). Following a link opens the
 * Transcript section (`openTranscript`), lands on the turn (`seg-<n>`: scroll, focus, a brief ring via
 * `flashSeg`), moves the audio there without playing, and bumps `reveal` when the words were in the English.
 */
export default function useEvidenceJump({ segments, analytics, speakerLabels = {}, audioRef, openTranscript }) {
  const index = useMemo(() => buildTranscriptIndex(segments), [segments]);
  const customerSpeaker = customerSpeakerOf(analytics);
  const [flashSeg, setFlashSeg] = useState(null);
  const [reveal, setReveal] = useState({ seg: null, n: 0 });

  const goToSegment = useCallback((seg, time, source) => {
    if (openTranscript) openTranscript();
    if (audioRef?.current && Number.isFinite(time)) audioRef.current.currentTime = time;
    setFlashSeg(seg);
    if (source === 'translation') setReveal((r) => ({ seg, n: r.n + 1 }));
    // A folded section opens on the next render; if the turn is still hidden then, wait one frame more.
    const land = (retry) => {
      const el = document.getElementById(`seg-${seg}`);
      if (el && el.offsetParent === null && retry) { nextFrame(() => land(false)); return; }
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: scrollBehaviour(), block: 'center' });
      if (el?.focus) el.focus({ preventScroll: true });
    };
    nextFrame(() => land(true));
  }, [audioRef, openTranscript]);

  useEffect(() => {
    if (flashSeg === null) return undefined;
    const t = setTimeout(() => setFlashSeg(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [flashSeg]);

  const sideOfCode = useCallback(
    (code) => sideOf(code, { customerSpeaker, labels: speakerLabels }),
    [customerSpeaker, speakerLabels],
  );
  // Which side a turn sits on, to prefer a match on the side of the speaker the scorer named.
  const sideOfSeg = useCallback((seg) => sideOfCode(index.segments[seg]?.SegmentSpeaker), [index, sideOfCode]);

  // Where each quote was found, worked out once per quote: the page asks on every render (a keystroke in an
  // Override reason re-renders every question), and a quote that is not found costs a fuzzy scan of the call.
  // A fresh cache whenever the transcript or its speakers change: the dependencies are what empty it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hits = useMemo(() => new Map(), [index, sideOfSeg]);
  const hitOf = useCallback((ev) => {
    const key = JSON.stringify([ev?.quote, ev?.speaker, ev?.start, ev?.seg, ev?.source]);
    if (!hits.has(key)) hits.set(key, locateEvidence(ev, index, { sideOf: sideOfSeg }));
    return hits.get(key);
  }, [hits, index, sideOfSeg]);

  const evidenceAt = useCallback((ev) => {
    const hit = hitOf(ev);
    if (!hit) return null;
    const code = index.segments[hit.seg]?.SegmentSpeaker;
    return {
      speaker: speakerLabels[code] || code || '',
      time: Formatter.Time(hit.time),
      isAgent: sideOfCode(code) === 'right',
      onClick: () => goToSegment(hit.seg, hit.time, hit.source),
    };
  }, [hitOf, index, sideOfCode, speakerLabels, goToSegment]);

  return { evidenceAt, goToSegment, flashSeg, reveal };
}
